import { Autocomplete, Box, Stack, TextField } from "@mui/material";
import { throttle } from "lodash";
import React, { useState, useEffect, useMemo, useRef } from "react";
import { getTblProductsSearch } from "../services/TblProductsServices";
import { buildPlanIndex, findCurrentPlan, searchPlans, suggestPlan } from "../utils/fuzzyRank";

const SubstringProductsSearch = ({
  searchValue,
  onProductSelected,
  onProductFound,
}) => {
  const [value, setValue] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [options, setOptions] = useState([]);
  const [errorText, setErrorText] = useState("");
  const [selectionError, setSelectionError] = useState("");

  useEffect(() => {
    if (searchValue) {
      setValue({ productCode: searchValue });
      setInputValue(searchValue);
    } else {
      setValue(null);
      setInputValue("");
    }
  }, [searchValue]);

  useEffect(() => {
    if (!inputValue || inputValue.length <= 2) {
      setErrorText("");
      setOptions([]);
    }
  }, [inputValue]);

  const fetch = useMemo(
    () =>
      throttle((request) => {
        if (request?.input.length > 2) {
          getTblProductsSearch(request.input)
            .then((res) => {
              const products = res.data || [];

              if (products.length === 0) {
                setOptions([]);
                setErrorText("Product not found");
                setSelectionError("");
                onProductFound?.(false);
              } else {
                setOptions(products);
                setErrorText("");
                onProductFound?.(true);
              }
            })
            .catch((err) => {
              console.error(err);
              setOptions([]);
              onProductFound?.(false);
            });
        }
      }, 200),
    []
  );

  useEffect(() => {
    if (!inputValue || inputValue.length <= 2) return;
    fetch({ input: inputValue });
  }, [inputValue, fetch]);
  return (
    <Autocomplete
      id="tbl-products-search"
      onBlur={() => {
        if (options.length > 0 && !value && inputValue.length > 2) {
          setSelectionError(
            "Please select a plan from the search results to continue."
          );
        }
      }}
      freeSolo
      sx={{ width: 250 }}
      options={options}
      value={value}
      inputValue={inputValue}
      onInputChange={(event, newInputValue, reason) => {
        setInputValue(newInputValue);
        setSelectionError("");

        if (reason === "input") {
          setValue(null);
          onProductSelected?.(null); // Disable Save/Submit
        }

        if (!newInputValue) {
          setOptions([]);
          setErrorText("");
        }
      }}
      onChange={(event, newValue) => {

        setValue(newValue);

        if (newValue) {
          setInputValue(newValue);
          setSelectionError("");
          onProductSelected?.(newValue); // Enable Save/Submit
        } else {
          onProductSelected?.(null);
        }
      }}
      getOptionLabel={(option) => option?.productCode || ""}
      isOptionEqualToValue={(option, value) =>
        option.productCode === value.productCode
      }
      noOptionsText="No Plan Id Found"
      filterOptions={(x) => x}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          label="Plan Id Search"
          error={!!errorText || !!selectionError}
          helperText={errorText || selectionError}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.id}>
          <Stack direction="column">
            <p>Plan Id : {option.productCode}</p>
          </Stack>
        </Box>
      )}
    />
  );
};

// ---------------------------------------------------------------- catalog mode (#418)
// The whole active catalog is fetched once per page load and indexed, and each keystroke is a
// plain search over it (utils/fuzzyRank.js searchPlans): code starts with, then code contains,
// then every typed word in the code or plan name, capped at 50. No edit-distance ranking: 99% of
// active codes have another active code one character away, and it took seconds per keystroke on
// the real catalog.
//
// On a Potential line the box opens empty -- the invoice SKU is not a plan, so it is never the
// search text -- and the one plan the SKU or item name clearly names, if any, is pinned first and
// labelled as a suggestion. It is never picked for the user.
//
// Opening on an existing agreement, its current plan is already the picked value (see
// findCurrentPlan), so leaving the box alone is valid. Clicking into the box selects its text, so
// the first keystroke replaces the current code instead of having to delete it first. The value
// can only be a row picked from the list, so whatever is saved is guaranteed to be an active plan
// code; typed text on its own never counts as a selection.
const NO_CATALOG = { rows: [], index: buildPlanIndex([]) };
let catalogRequest = null;
const loadCatalog = () => {
  if (!catalogRequest) {
    catalogRequest = getTblProductsSearch("", { active: true })
      .then((res) => ({ rows: res.data, index: buildPlanIndex(res.data) }))
      .catch((err) => {
        catalogRequest = null; // let the next mount retry
        throw err;
      });
  }
  return catalogRequest;
};

const SUGGESTED_LABEL = "Suggested from the invoice SKU";

// requirePick: the text in the box is not a plan at all (a Potential line's invoice SKU), so
// nothing is pre-selected and only a row picked from the list counts. suggestSku and
// suggestItemName are that line's SKU and item name, for the pinned suggestion. lockedNote, when
// set, freezes the box on its plan and says why in place of the helper text.
const FuzzyProductsSearch = ({
  lockedNote,
  searchValue,
  currentProductId,
  requirePick = false,
  suggestSku,
  suggestItemName,
  onProductSelected,
  onProductFound,
}) => {
  const [catalog, setCatalog] = useState(NO_CATALOG);
  const [loadError, setLoadError] = useState("");
  const [value, setValue] = useState(null);
  // On a Potential line, searchValue is the invoice SKU. It isn't a plan, so it's never the search text.
  const [inputValue, setInputValue] = useState(requirePick ? "" : searchValue || "");
  // Set once the user types, so the settle below never overwrites their text.
  const typed = useRef(false);

  // The dialog sets its plan code a render after mounting this box, and the catalog arrives
  // asynchronously, so settle the starting state when either does: the current plan picked if it
  // is in the catalog, otherwise the stored text (a retired plan). Never over a plan already
  // picked, whose own code comes back through searchValue, nor over text typed before the
  // catalog arrived (it is not cached until the first open).
  // Not reported through onProductSelected: the dialog already holds this plan's details, and
  // that callback is what resets the equipment limit and renewal checks for a changed plan.
  useEffect(() => {
    if (value || typed.current) return;
    const current = requirePick ? null : findCurrentPlan(catalog.rows, searchValue, currentProductId);
    setValue(current);
    setInputValue(current ? current.productCode : requirePick ? "" : searchValue || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalog, searchValue]);

  useEffect(() => {
    let live = true;
    // Without the catalog nothing can be picked. That only blocks saving when a pick is required
    // (a Potential line); an existing agreement keeps its stored plan and can still be saved, so a
    // failing catalog load never locks dealers out of every edit.
    loadCatalog()
      .then((loaded) => {
        if (!live) return;
        setCatalog(loaded);
        if (requirePick) onProductFound?.(loaded.rows.length > 0);
      })
      .catch((err) => {
        console.error("Unable to load the plan catalog", err);
        if (!live) return;
        setLoadError(
          requirePick
            ? "The plan list could not be loaded."
            : "The plan list could not be loaded. The current plan is kept."
        );
        if (requirePick) onProductFound?.(false);
      });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const suggested = useMemo(
    () => (requirePick ? suggestPlan(catalog.index, suggestSku, suggestItemName) : null),
    [catalog, requirePick, suggestSku, suggestItemName]
  );
  const options = useMemo(() => {
    const hits = searchPlans(catalog.index, inputValue);
    return suggested ? [suggested, ...hits.filter((p) => p !== suggested)].slice(0, 50) : hits;
  }, [catalog, inputValue, suggested]);

  return (
    <Autocomplete
      id="tbl-products-search"
      sx={{ width: 250 }}
      disabled={Boolean(lockedNote)}
      options={options}
      value={value}
      inputValue={inputValue}
      // No autoHighlight: most codes have a look-alike one character away, and the suggestion is
      // pinned first, so Enter must never pick a plan. A pick is a click or an arrow-key choice.
      openOnFocus
      selectOnFocus
      clearOnBlur={false}
      // At most 50 plans; this just keeps them a scrollable panel.
      ListboxProps={{ style: { maxHeight: 320, overflow: "auto" } }}
      filterOptions={(x) => x}
      onInputChange={(event, newInputValue, reason) => {
        setInputValue(newInputValue);
        if (reason === "input") typed.current = true;
        if (reason === "input" && value) {
          setValue(null);
          onProductSelected?.(null);
        }
      }}
      onChange={(event, newValue) => {
        setValue(newValue);
        onProductSelected?.(newValue || null);
      }}
      getOptionLabel={(option) => option?.productCode || ""}
      isOptionEqualToValue={(option, selected) => option.productCode === selected.productCode}
      noOptionsText={catalog.rows.length ? "No plan matches that" : "Loading plans..."}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="standard"
          label="Plan Id Search"
          error={!!loadError}
          helperText={lockedNote || loadError || (value ? value.codeName : "Pick the plan from the list")}
        />
      )}
      renderOption={(props, option) => (
        <Box component="li" {...props} key={option.productCode}>
          <Stack direction="column">
            <span>{option.productCode}</span>
            <span style={{ fontSize: "0.75rem", opacity: 0.7 }}>{option.codeName}</span>
            {option === suggested && (
              <span style={{ fontSize: "0.75rem", fontWeight: 600 }}>{SUGGESTED_LABEL}</span>
            )}
          </Stack>
        </Box>
      )}
    />
  );
};

// fuzzy: search the active catalog in the browser as the user types, and accept only a picked
// row. Off by default, so every existing caller keeps the server-side substring search it has
// today.
const TblProductsSearch = ({ fuzzy = false, ...props }) =>
  fuzzy ? <FuzzyProductsSearch {...props} /> : <SubstringProductsSearch {...props} />;

export default TblProductsSearch;